// (2026-04-23, Jason Cox) Restock Events page. Consumes the existing
// scm/v1/listTransferTicket endpoint (backend-unchanged). Structure
// mirrors ConsumptionEvents (DeliveryTickets.tsx) but for transfer
// tickets, which record restocks/deliveries INTO smartboxes rather than
// consumption out.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import type { TransferTicketRow } from '../services/unifiedApiService';
import { useSmartBoxList } from '../hooks/useSmartBoxList';
import { TruckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { parseMoney, formatMoney } from '../utils/format';
import { PRESET_LABELS, RangePreset, rangeForPreset, todayYmd } from '../utils/dateRanges';

// ---------- component ----------

const SUMMARY_PAGE_SIZE = 25;
const UI_PAGE_SIZE = 25;

const RestockEvents: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();
  const api = useUnifiedApi();

  const [preset, setPreset] = useState<RangePreset>('today');
  const [customStart, setCustomStart] = useState<string>(todayYmd());
  const [customEnd, setCustomEnd] = useState<string>(todayYmd());

  const { smartboxes, loading: smartboxesLoading } = useSmartBoxList();
  const [smartboxId, setSmartboxId] = useState<number | ''>('');

  // (2026-04-28) SKU search, debounced 400ms before firing the API.
  const [skuSearch, setSkuSearch] = useState('');
  const [appliedSku, setAppliedSku] = useState('');

  const [rows, setRows] = useState<TransferTicketRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<number | null>(null);

  const [uiPage, setUiPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { startDate, endDate } = useMemo(
    () => rangeForPreset(preset, customStart, customEnd),
    [preset, customStart, customEnd],
  );

  const load = useCallback(async () => {
    if (!api || !api.getTransferTicketList) return;
    // (2026-09-09, Jason Cox) See DeliveryTickets: the render guard returns
    // <Navigate>, but effects registered above it still fire on mount, so the
    // fetch needs its own permission check.
    if (!permissions.canViewRestockEvents) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getTransferTicketList({
        page: 1,
        pageSize: SUMMARY_PAGE_SIZE,
        startTime: startDate,
        endTime: endDate,
        smartboxId: smartboxId === '' ? undefined : smartboxId,
        sku: appliedSku.trim() || undefined,
        sort: 2,
        field: 'createAt',
      });
      setRows(res.data);
      setTotalRows(res.total);
      setUiPage(1);
      setLastFetched(Date.now());
    } catch (e: any) {
      setError(e?.message || 'Failed to load restock events');
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [api, permissions.canViewRestockEvents, startDate, endDate, smartboxId, appliedSku]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate, smartboxId, appliedSku]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedSku(skuSearch), 400);
    return () => clearTimeout(t);
  }, [skuSearch]);

  const summary = useMemo(() => {
    let spend = 0;
    let qty = 0;
    for (const r of rows) {
      spend += parseMoney(r.total);
      qty += r.onhand || 0;
    }
    return { count: rows.length, spend, qty, truncated: totalRows > rows.length };
  }, [rows, totalRows]);

  const pageSlice = useMemo(() => {
    const start = (uiPage - 1) * UI_PAGE_SIZE;
    return rows.slice(start, start + UI_PAGE_SIZE);
  }, [rows, uiPage]);
  const totalUiPages = Math.max(1, Math.ceil(rows.length / UI_PAGE_SIZE));

  if (user?.system !== 'smart') {
    return (
      <Layout>
        <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary-600">Restock Events is available for Smart users only.</p>
        </div>
      </Layout>
    );
  }

  // (2026-09-09, Jason Cox) Guard: see DeliveryTickets -- the tile is hidden
  // without canViewRestockEvents but /restock-events was still reachable by URL.
  if (!permissions.canViewRestockEvents) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-secondary-900 sm:text-3xl sm:truncate flex items-center">
                <TruckIcon className="h-8 w-8 mr-2 text-primary-600" />
                Restock Events
              </h2>
              <p className="mt-1 text-sm text-secondary-600">
                Transfer tickets for items restocked INTO smartboxes within the selected
                range, with a running total value. Defaults to today.
              </p>
            </div>
            <div className="mt-3 md:mt-0 flex items-center space-x-2">
              {lastFetched && (
                <span className="text-xs text-secondary-500">
                  Last refreshed: {new Date(lastFetched).toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={load}
                disabled={loading}
                className="btn-secondary flex items-center"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filter card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-secondary-700">Range</label>
                  <select
                    value={preset}
                    onChange={(e) => setPreset(e.target.value as RangePreset)}
                    className="input mt-1 w-full"
                  >
                    {(Object.keys(PRESET_LABELS) as RangePreset[]).map(k => (
                      <option key={k} value={k}>{PRESET_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
                {preset === 'custom' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-secondary-700">Start date</label>
                      <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="input mt-1 w-full" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-secondary-700">End date</label>
                      <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="input mt-1 w-full" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs font-medium text-secondary-700">SmartBox</label>
                  <select
                    value={smartboxId}
                    onChange={(e) => setSmartboxId(e.target.value ? parseInt(e.target.value, 10) : '')}
                    className="input mt-1 w-full"
                    disabled={smartboxesLoading}
                  >
                    <option value="">
                      {smartboxesLoading ? 'Loading SmartBoxes...' : `All SmartBoxes${smartboxes.length ? ` (${smartboxes.length})` : ''}`}
                    </option>
                    {smartboxes.map((sb) => (
                      <option key={sb.id} value={sb.id}>
                        {sb.code}{sb.name && sb.name !== sb.code ? ` -- ${sb.name}` : ''}
                        {sb.wName ? ` @ ${sb.wName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-secondary-700">Search SKU</label>
                  <input
                    type="text"
                    value={skuSearch}
                    onChange={(e) => setSkuSearch(e.target.value)}
                    placeholder="e.g. WIDGET-001"
                    className="input mt-1 w-full"
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-xs text-secondary-600 w-full text-right">
                    <div>{startDate} &rarr; {endDate}</div>
                    {smartboxId !== '' && (
                      <div className="text-secondary-500">
                        Filtered to {smartboxes.find(s => s.id === smartboxId)?.code || `smartbox ${smartboxId}`}
                      </div>
                    )}
                    {appliedSku.trim() !== '' && (
                      <div className="text-secondary-500">
                        SKU: {appliedSku.trim()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="card p-4">
              <div className="text-xs text-secondary-500">Total value restocked</div>
              <div className="text-2xl font-bold text-secondary-900">{formatMoney(summary.spend)}</div>
              {summary.truncated && (
                <div className="mt-1 text-xs text-orange-700">
                  Covers first {rows.length} of {totalRows} rows -- narrow the range for an exact total.
                </div>
              )}
            </div>
            <div className="card p-4">
              <div className="text-xs text-secondary-500">Restock lines</div>
              <div className="text-2xl font-bold text-secondary-900">{totalRows.toLocaleString()}</div>
              <div className="mt-1 text-xs text-secondary-500">
                Showing {rows.length.toLocaleString()} loaded / {summary.qty.toLocaleString()} units
              </div>
            </div>
            <div className="card p-4">
              <div className="text-xs text-secondary-500">Date range</div>
              <div className="text-lg font-semibold text-secondary-900">{PRESET_LABELS[preset]}</div>
              <div className="mt-1 text-xs text-secondary-500">{startDate} through {endDate}</div>
            </div>
          </div>

          {error && (
            <div className="rounded-md p-3 bg-red-50 text-red-800 text-sm mb-4">{error}</div>
          )}

          {/* Restock table */}
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 text-sm">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Doc #</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Date</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Item</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Qty</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Price</th>
                  <th className="px-3 py-2 text-right font-medium text-secondary-700">Total</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">SmartBox</th>
                  <th className="px-3 py-2 text-left font-medium text-secondary-700">Workcenter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-secondary-500">Loading...</td></tr>
                )}
                {!loading && pageSlice.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-secondary-500">No restock events in this range.</td></tr>
                )}
                {!loading && pageSlice.map((row) => {
                  const isExpanded = expandedId === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr className="hover:bg-secondary-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : row.id)}>
                        <td className="px-3 py-2 font-mono text-xs">{row.docNum}</td>
                        <td className="px-3 py-2 text-secondary-700">{typeof row.createAt === 'string' ? row.createAt : ''}</td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs text-secondary-900">{row.sku || '—'}</div>
                          <div className="text-xs text-secondary-500">{row.itemName || ''}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{row.onhand}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatMoney(parseMoney(row.price))}</td>
                        <td className="px-3 py-2 text-right font-mono font-medium">{formatMoney(parseMoney(row.total))}</td>
                        <td className="px-3 py-2 text-secondary-700">{row.smartboxName || '—'}</td>
                        <td className="px-3 py-2 text-secondary-700">{row.workcenterName || '—'}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-secondary-50">
                          <td colSpan={8} className="px-3 py-3">
                            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              <div><dt className="text-secondary-500">Vendor</dt><dd className="text-secondary-900">{row.companyName || '—'}</dd></div>
                              <div><dt className="text-secondary-500">Vendor code</dt><dd className="text-secondary-900">{row.companyCode || '—'}</dd></div>
                              <div><dt className="text-secondary-500">Locked by</dt><dd className="text-secondary-900">{row.lockUser || '—'}</dd></div>
                              <div><dt className="text-secondary-500">Client item code</dt><dd className="text-secondary-900">{row.clientItemCode || '—'}</dd></div>
                              <div><dt className="text-secondary-500">Ticket ID</dt><dd className="text-secondary-900 font-mono">{row.id}</dd></div>
                              <div><dt className="text-secondary-500">Head ID</dt><dd className="text-secondary-900 font-mono">{row.headId || '—'}</dd></div>
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalUiPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="text-secondary-600">Page {uiPage} of {totalUiPages} ({rows.length} loaded)</div>
              <div className="space-x-2">
                <button onClick={() => setUiPage(p => Math.max(1, p - 1))} disabled={uiPage <= 1} className="btn-secondary disabled:opacity-50">Previous</button>
                <button onClick={() => setUiPage(p => Math.min(totalUiPages, p + 1))} disabled={uiPage >= totalUiPages} className="btn-secondary disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RestockEvents;
